import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { fetchCurrentSubscription } from '../services/subscriptions';
import type { AuthBootstrapSubscriptionState, SubscriptionPlanType, SubscriptionSummary } from '../types';
import { getApiErrorStatus } from '../utils/errors';

interface UseCurrentSubscriptionOptions {
  subscriptionType?: SubscriptionPlanType;
  permissionCode?: string;
}

/**
 * Resolve the bootstrap subscription state that matches the requested plan type.
 *
 * When no specific type is requested, the detailed endpoint still owns the
 * selection logic, so the bootstrap is only authoritative for "no active
 * subscription at all" scenarios.
 */
function resolveBootstrapSubscriptionState(
  subscriptions: {
    ai: AuthBootstrapSubscriptionState;
    reverseIvr: AuthBootstrapSubscriptionState;
  } | null,
  subscriptionType?: SubscriptionPlanType,
): AuthBootstrapSubscriptionState | null {
  if (!subscriptions || !subscriptionType) {
    return null;
  }
  return subscriptionType === 'reverse_ivr'
    ? subscriptions.reverseIvr
    : subscriptions.ai;
}

/**
 * Load the current subscription summary when permitted.
 */
export function useCurrentSubscription(options: UseCurrentSubscriptionOptions = {}) {
  const { subscriptionType, permissionCode = 'subscriptions_read' } = options;
  const { isLoading: authLoading, subscriptions } = useAuth();
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const canRead = useMemo(() => hasPermission(permissionCode), [hasPermission, permissionCode]);
  const bootstrapSubscriptionState = useMemo(
    () => resolveBootstrapSubscriptionState(subscriptions, subscriptionType),
    [subscriptionType, subscriptions],
  );
  const bootstrapConfirmsNoSubscription = useMemo(() => {
    if (!subscriptions) {
      return false;
    }
    if (bootstrapSubscriptionState) {
      return bootstrapSubscriptionState.canRead && !bootstrapSubscriptionState.hasActive;
    }
    return (
      subscriptions.ai.canRead
      && subscriptions.reverseIvr.canRead
      && !subscriptions.ai.hasActive
      && !subscriptions.reverseIvr.hasActive
    );
  }, [bootstrapSubscriptionState, subscriptions]);
  const [subscription, setSubscription] = useState<SubscriptionSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const loadSubscription = useCallback(async () => {
    if (!canRead || bootstrapConfirmsNoSubscription) {
      setSubscription(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCurrentSubscription(subscriptionType);
      setSubscription(data);
    } catch (err: unknown) {
      if (getApiErrorStatus(err) === 404) {
        setSubscription(null);
        setError(null);
        return;
      }
      setSubscription(null);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [bootstrapConfirmsNoSubscription, canRead, subscriptionType]);

  useEffect(() => {
    if (authLoading || permissionsLoading) {
      return;
    }
    if (!canRead || bootstrapConfirmsNoSubscription) {
      setSubscription(null);
      setError(null);
      setLoading(false);
      return;
    }
    loadSubscription();
  }, [authLoading, bootstrapConfirmsNoSubscription, canRead, loadSubscription, permissionsLoading]);

  return {
    subscription,
    loading,
    error,
    refresh: loadSubscription,
    isReady: canRead && !authLoading && !permissionsLoading,
  };
}
